import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const fmt = (v) => {
  if (v == null || isNaN(v)) return '—';
  return `${Math.round(v).toLocaleString('fr-FR')} €`;
};

const colorForVariance = (v, type) => {
  // For costs: under budget = good (green), over = bad (red)
  // For revenue/profit: over = good, under = bad
  if (v == null) return '#71717A';
  const positive = type === 'cost' || type === 'tax' ? v <= 0 : v >= 0;
  const big = Math.abs(v) > 10;
  if (v === 0) return '#71717A';
  return positive
    ? (big ? '#10B981' : '#84CC16')
    : (big ? '#EF4444' : '#F59E0B');
};

const VarianceBar = ({ estimated, real, type, variance }) => {
  const max = Math.max(Math.abs(estimated || 0), Math.abs(real || 0), 1);
  const estWidth = Math.min(100, (Math.abs(estimated || 0) / max) * 100);
  const realWidth = Math.min(100, (Math.abs(real || 0) / max) * 100);
  const color = colorForVariance(variance, type);

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-zinc-500 w-12">Estimé</span>
        <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${estWidth}%`, backgroundColor: '#3B82F6' }} />
        </div>
        <span className="text-xs font-mono text-zinc-400 w-24 text-right">{fmt(estimated)}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-zinc-500 w-12">Réel</span>
        <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${realWidth}%`, backgroundColor: color }} />
        </div>
        <span className="text-xs font-mono w-24 text-right" style={{ color }}>{fmt(real)}</span>
      </div>
    </div>
  );
};

export const ProjectComparisonDialog = ({ projectId, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !projectId) return;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/comparison`, { withCredentials: true });
        setData(res.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [open, projectId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">Comparatif Étude vs Réalité</DialogTitle>
        </DialogHeader>
        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
          </div>
        )}
        {!loading && data && (
          <div className="space-y-3">
            {data.items.map(item => {
              const v = item.variance_pct;
              const Icon = v == null || v === 0 ? Minus : (v > 0 ? TrendingUp : TrendingDown);
              const tagColor = colorForVariance(v, item.type);
              return (
                <div key={item.key} className="p-3 rounded-lg bg-zinc-800/40 border border-zinc-800" data-testid={`comparison-${item.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-white">{item.label}</span>
                    <span className="text-xs font-mono inline-flex items-center gap-1 px-2 py-0.5 rounded" style={{ color: tagColor, backgroundColor: tagColor + '20' }}>
                      <Icon className="w-3 h-3" />
                      {v == null ? 'N/A' : `${v > 0 ? '+' : ''}${v}%`}
                    </span>
                  </div>
                  <VarianceBar estimated={item.estimated} real={item.real} type={item.type} variance={v} />
                </div>
              );
            })}

            {data.planning && (
              <div className="p-4 rounded-lg bg-zinc-800/40 border border-zinc-800 mt-4">
                <h4 className="text-sm font-semibold text-white mb-3">Planning</h4>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-zinc-500">Durée travaux estimée</div>
                    <div className="text-white font-mono">{data.planning.duree_travaux_sem_estimee || '—'} sem</div>
                  </div>
                  <div>
                    <div className="text-zinc-500">Durée travaux réelle</div>
                    <div className="text-white font-mono">{data.planning.duree_travaux_sem_reelle || '—'} sem</div>
                  </div>
                  <div>
                    <div className="text-zinc-500">Date revente estimée</div>
                    <div className="text-white font-mono">{data.planning.date_revente_estimee || '—'}</div>
                  </div>
                  <div>
                    <div className="text-zinc-500">Date vente réelle</div>
                    <div className="text-white font-mono">{data.planning.date_vente_reelle || '—'}</div>
                  </div>
                  <div>
                    <div className="text-zinc-500">Prix mise en vente</div>
                    <div className="text-white font-mono">{fmt(data.planning.prix_mise_en_vente)}</div>
                  </div>
                  <div>
                    <div className="text-zinc-500">Visites / Offres</div>
                    <div className="text-white font-mono">{data.planning.nb_visites} / {data.planning.nb_offres}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
